import { c as _c } from "react/compiler-runtime";
import { Button, Field, HStack, Input, InputGroup, Stat, Text, VStack } from '@chakra-ui/react';
import { ClipboardIconButton, ClipboardInput, ClipboardRoot } from '@src/components/chakra-ui/clipboard';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@src/components/chakra-ui/dialog';
import { useState } from 'react';
import { LuExternalLink, LuRefreshCw, LuSave } from 'react-icons/lu';
export type ProfileLinksEditorProps = {
  profileUrl: string | null;
  bookUrl: string;
  complimentaryUrl: string;
  waiveCode: string;
  waiveInvalid: boolean;
  error: string | null;
  saved: boolean;
  saving: boolean;
  onWaiveCodeChange: (value: string) => void;
  onSave: () => void;
  /** L18: sessions booked through the share link (`link` + `link-waive`). */
  bookingLinkSessionsCount?: number | null;
  /** L17: replace the waive code; the old one stops working. Omit to hide Rotate. */
  onRotate?: () => void | Promise<void>;
  rotating?: boolean;
  rotated?: boolean;
  /** L17 "Preview as guest": the booking page, opened in a new tab. */
  previewUrl?: string | null;
};
const LinkRow = t0 => {
  const $ = _c(10);
  const {
    label,
    description,
    url
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Field.Label>{label}</Field.Label>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== url) {
    t2 = url ? <ClipboardRoot value={url} width="full"><InputGroup endElement={<ClipboardIconButton />} width="full"><ClipboardInput readOnly={true} /></InputGroup></ClipboardRoot> : <Input disabled={true} placeholder="Your public profile slug is not set yet" />;
    $[2] = url;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <Field.HelperText>{description}</Field.HelperText>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <Field.Root>{t1}{t2}{t3}</Field.Root>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const ProfileLinksEditor = t0 => {
  const $ = _c(64);
  const {
    profileUrl,
    bookUrl,
    complimentaryUrl,
    waiveCode,
    waiveInvalid,
    error,
    saved,
    saving,
    onWaiveCodeChange,
    onSave,
    bookingLinkSessionsCount,
    onRotate,
    rotating: t1,
    rotated: t2,
    previewUrl
  } = t0;
  const rotating = t1 === undefined ? false : t1;
  const rotated = t2 === undefined ? false : t2;
  const [rotateOpen, setRotateOpen] = useState(false);
  let t3;
  if ($[0] !== bookingLinkSessionsCount) {
    t3 = bookingLinkSessionsCount != null ? <Stat.Root borderWidth="1px" padding={4} rounded="l2"><Stat.Label>Bookings from your link</Stat.Label><Stat.ValueText fontSize="2xl">{bookingLinkSessionsCount}</Stat.ValueText><Stat.HelpText>{bookingLinkSessionsCount === 1 ? "1 booking" : `${bookingLinkSessionsCount} bookings`} from your link, with or without the code.</Stat.HelpText></Stat.Root> : null;
    $[0] = bookingLinkSessionsCount;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== profileUrl) {
    t4 = <LinkRow description="Anyone can open your member profile from this address." label="Profile" url={profileUrl} />;
    $[2] = profileUrl;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== bookUrl) {
    t5 = <LinkRow label="Book a session" url={bookUrl} description="Opens your member profile with booking on the page. Guests register there and pay your rate." />;
    $[4] = bookUrl;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== previewUrl) {
    t6 = previewUrl ? <VStack align="stretch" gap={1}><Button asChild={true} alignSelf="flex-start" size="sm" variant="outline"><a href={previewUrl} rel="noopener noreferrer" target="_blank"><LuExternalLink /> Preview as guest</a></Button><Text color="colorPalette.fg/60" fontFamily="longform" textStyle="xs">Opens your booking page in a new tab. You are signed in there too, so open the link in a private window to see exactly what a signed-out guest sees.</Text></VStack> : null;
    $[6] = previewUrl;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Field.Label>Complimentary coupon</Field.Label>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== onWaiveCodeChange) {
    t8 = event => onWaiveCodeChange(event.currentTarget.value);
    $[9] = onWaiveCodeChange;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8 || $[12] !== waiveCode) {
    t9 = <Input maxLength={32} placeholder="friends-2026" value={waiveCode} onChange={t8} />;
    $[11] = t8;
    $[12] = waiveCode;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Field.HelperText>This code makes the session free. Letters, numbers, and hyphens; anyone who books with it skips session fees.</Field.HelperText>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== waiveInvalid) {
    t11 = waiveInvalid ? <Field.ErrorText>Use 3–32 characters: lowercase letters, numbers, hyphens.</Field.ErrorText> : null;
    $[15] = waiveInvalid;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t9 || $[19] !== waiveInvalid) {
    t12 = <Field.Root invalid={waiveInvalid}>{t7}{t9}{t10}{t11}</Field.Root>;
    $[17] = t11;
    $[18] = t9;
    $[19] = waiveInvalid;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Field.Label>Complimentary booking</Field.Label>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ClipboardIconButton />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <InputGroup endElement={t14} width="full"><ClipboardInput readOnly={true} /></InputGroup>;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== complimentaryUrl) {
    t16 = <ClipboardRoot value={complimentaryUrl} width="full">{t15}</ClipboardRoot>;
    $[24] = complimentaryUrl;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Field.HelperText>Save the coupon before you share this link so it will honor the code.</Field.HelperText>;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t16) {
    t18 = <Field.Root>{t13}{t16}{t17}</Field.Root>;
    $[27] = t16;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t12 || $[30] !== t18) {
    t19 = <VStack align="stretch" gap={4}>{t12}{t18}</VStack>;
    $[29] = t12;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== error) {
    t20 = error ? <Text color="red.fg" fontFamily="longform" fontSize="sm">{error}</Text> : null;
    $[32] = error;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== saved) {
    t21 = saved ? <Text color="green.fg" fontFamily="longform" fontSize="sm">Complimentary link saved.</Text> : null;
    $[34] = saved;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== rotated) {
    t22 = rotated ? <Text color="green.fg" fontFamily="longform" fontSize="sm">New code ready. The old complimentary link no longer works.</Text> : null;
    $[36] = rotated;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <LuSave />;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== onSave || $[40] !== saving || $[41] !== waiveInvalid) {
    t24 = <Button disabled={waiveInvalid} loading={saving} variant="cta" onClick={onSave}>{t23} Save complimentary link</Button>;
    $[39] = onSave;
    $[40] = saving;
    $[41] = waiveInvalid;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== onRotate || $[44] !== rotating) {
    t25 = onRotate ? <Button loading={rotating} variant="outline" onClick={() => setRotateOpen(true)}><LuRefreshCw /> Rotate code</Button> : null;
    $[43] = onRotate;
    $[44] = rotating;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t24 || $[47] !== t25) {
    t26 = <HStack gap={3} wrap="wrap">{t24}{t25}</HStack>;
    $[46] = t24;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== onRotate || $[50] !== rotateOpen || $[51] !== rotating) {
    t27 = onRotate ? <DialogRoot lazyMount={true} unmountOnExit={true} open={rotateOpen} placement="center" onOpenChange={t28 => {
      const {
        open
      } = t28;
      return setRotateOpen(open);
    }}><DialogContent maxWidth="md"><DialogCloseTrigger /><DialogHeader pr={10}><DialogTitle>Rotate your complimentary code?</DialogTitle></DialogHeader><DialogBody><DialogDescription fontFamily="longform">We replace the code with a new random one. Anyone holding the old complimentary link will pay your standard rate. Bookings already made are not affected.</DialogDescription></DialogBody><DialogFooter><Button variant="ghost" onClick={() => setRotateOpen(false)}>Keep current code</Button><Button loading={rotating} variant="cta" onClick={async () => {
            await onRotate();
            setRotateOpen(false);
          }}>Rotate code</Button></DialogFooter></DialogContent></DialogRoot> : null;
    $[49] = onRotate;
    $[50] = rotateOpen;
    $[51] = rotating;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t19 || $[54] !== t20 || $[55] !== t21 || $[56] !== t22 || $[57] !== t26 || $[58] !== t27 || $[59] !== t3 || $[60] !== t4 || $[61] !== t5 || $[62] !== t6) {
    t28 = <VStack align="stretch" gap={6}>{t3}{t4}{t5}{t6}{t19}{t20}{t21}{t22}{t26}{t27}</VStack>;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
    $[57] = t26;
    $[58] = t27;
    $[59] = t3;
    $[60] = t4;
    $[61] = t5;
    $[62] = t6;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  return t28;
};